import { useState, useEffect, useCallback, useRef } from 'react';
import { X, Loader2, Smartphone, ArrowLeft } from 'lucide-react';
import { InputOTP, InputOTPGroup, InputOTPSlot } from './ui/input-otp';
import { Button } from './ui/button';
import { phoneAuthService } from '../../services/phoneAuthService';
import { useAuthStore } from '../../store/authStore';
import { isUnregisteredPhone } from '../../utils/phoneAuthErrors';

interface PhoneLoginModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  /** OTP was valid but no account exists — hand the verified number off to signup. */
  onNeedsRegistration?: (phone: string, phoneIdToken: string) => void;
}

export function PhoneLoginModal({
  open,
  onOpenChange,
  onSuccess,
  onNeedsRegistration,
}: PhoneLoginModalProps) {
  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [resendCountdown, setResendCountdown] = useState(60);
  const [isResending, setIsResending] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const verificationIdRef = useRef<string | null>(null);
  // Guards against double completion when manual entry and auto-verification race.
  const completedRef = useRef(false);
  const phoneOtpLogin = useAuthStore((s) => s.phoneOtpLogin);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setStep('phone');
      setPhone('');
      setCode('');
      setResendCountdown(60);
      setError(null);
      setIsSending(false);
      setIsVerifying(false);
      verificationIdRef.current = null;
      completedRef.current = false;
      // Drop any pending auto-verification listener.
      void phoneAuthService.cleanup();
    }
  }, [open]);

  // Countdown timer for OTP step
  useEffect(() => {
    if (!open || step !== 'otp') return;
    const timer = setInterval(() => {
      setResendCountdown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [open, step]);

  const isValidPhone = /^[6-9]\d{9}$/.test(phone);

  const handleAutoVerifiedLogin = async () => {
    if (completedRef.current) return;
    completedRef.current = true;
    setIsVerifying(true);
    setError(null);
    let idToken: string | null = null;
    try {
      idToken = await phoneAuthService.getIdToken();
      await phoneAuthService.signOut();
      await phoneOtpLogin(idToken);
      onSuccess();
    } catch (err: any) {
      completedRef.current = false; // allow another attempt
      if (idToken && onNeedsRegistration && isUnregisteredPhone(err)) {
        onNeedsRegistration(phone, idToken);
        return;
      }
      const message = err?.response?.data?.message || err?.message || 'Login failed. Please try again.';
      setError(message);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSendOtp = async () => {
    if (!isValidPhone) return;
    setIsSending(true);
    setError(null);
    try {
      const result = await phoneAuthService.sendVerificationCode('+91' + phone, () => {
        void handleAutoVerifiedLogin();
      });
      if (result.autoVerified) {
        await handleAutoVerifiedLogin();
        return;
      }
      verificationIdRef.current = result.verificationId;
      setStep('otp');
      setResendCountdown(60);
    } catch (err: any) {
      console.error('[PhoneLoginModal] Failed to send SMS', err);
      setError(err?.message || 'Failed to send verification code. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const handleComplete = useCallback(
    async (value: string) => {
      if (value.length !== 6 || !verificationIdRef.current) return;
      if (completedRef.current) return;
      completedRef.current = true;
      setIsVerifying(true);
      setError(null);
      let idToken: string | null = null;
      try {
        idToken = await phoneAuthService.confirmCode(verificationIdRef.current, value);
        await phoneAuthService.signOut();
        await phoneOtpLogin(idToken);
        onSuccess();
      } catch (err: any) {
        console.error('[PhoneLoginModal] Login failed', err);
        completedRef.current = false; // allow retry with a new code
        if (idToken && onNeedsRegistration && isUnregisteredPhone(err)) {
          onNeedsRegistration(phone, idToken);
          return;
        }
        const message = err?.response?.data?.message || err?.message || 'Login failed. Please try again.';
        setError(message);
        setCode('');
      } finally {
        setIsVerifying(false);
      }
    },
    [phoneOtpLogin, onSuccess, onNeedsRegistration, phone],
  );

  const handleResend = async () => {
    setIsResending(true);
    setError(null);
    try {
      const result = await phoneAuthService.sendVerificationCode('+91' + phone, () => {
        void handleAutoVerifiedLogin();
      });
      if (result.autoVerified) {
        await handleAutoVerifiedLogin();
        return;
      }
      verificationIdRef.current = result.verificationId;
      setResendCountdown(60);
      setCode('');
    } catch (err: any) {
      setError(err?.message || 'Failed to resend code.');
    } finally {
      setIsResending(false);
    }
  };

  // Mask phone: 98****3210
  const maskedPhone = (() => {
    if (phone.length < 4) return phone;
    return phone.slice(0, 2) + '****' + phone.slice(-4);
  })();

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-[2000] flex items-end sm:items-center justify-center">
      <div className="bg-white dark:bg-gray-900 rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md max-h-[90vh] overflow-auto pb-[env(safe-area-inset-bottom)]">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 py-4 rounded-t-3xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {step === 'otp' && (
                <button
                  onClick={() => { setStep('phone'); setCode(''); setError(null); }}
                  disabled={isVerifying}
                  className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700 transition-all disabled:opacity-50"
                >
                  <ArrowLeft className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                </button>
              )}
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Login with OTP</h2>
            </div>
            <button
              onClick={() => onOpenChange(false)}
              disabled={isVerifying || isSending}
              className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-700 transition-all disabled:opacity-50"
            >
              <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 flex flex-col items-center">
          {/* Phone Icon */}
          <div className="w-16 h-16 rounded-full bg-[#8bc43e]/10 flex items-center justify-center mb-4">
            <Smartphone className="w-8 h-8 text-[#8bc43e]" />
          </div>

          {step === 'phone' ? (
            <>
              <p className="text-center text-gray-600 dark:text-gray-300 mb-6">
                Enter your phone number to receive a verification code
              </p>

              {/* Phone Input */}
              <div className="w-full mb-6">
                <div className="flex items-center border-2 border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden focus-within:border-[#8bc43e] transition-colors">
                  <span className="px-4 py-4 bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 font-medium border-r border-gray-200 dark:border-gray-700">
                    +91
                  </span>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setPhone(val);
                      setError(null);
                    }}
                    placeholder="Enter phone number"
                    className="flex-1 px-4 py-4 bg-transparent text-gray-900 dark:text-white placeholder-gray-400 outline-none text-lg"
                    autoFocus
                  />
                </div>
                {phone.length > 0 && !isValidPhone && (
                  <p className="text-xs text-red-500 mt-1 ml-1">Enter a valid 10-digit mobile number</p>
                )}
              </div>

              {/* Error */}
              {error && (
                <div className="w-full bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3 mb-4">
                  <p className="text-sm text-red-600 dark:text-red-400 text-center">{error}</p>
                </div>
              )}

              {/* Send OTP Button */}
              <Button
                onClick={handleSendOtp}
                disabled={!isValidPhone || isSending}
                className="w-full h-14 bg-[#8bc43e] hover:bg-[#7ab32e] text-white rounded-xl font-semibold disabled:opacity-50"
              >
                {isSending ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Sending...</span>
                  </div>
                ) : (
                  'Send OTP'
                )}
              </Button>
            </>
          ) : (
            <>
              <p className="text-center text-gray-600 dark:text-gray-300 mb-2">
                We sent a 6-digit code to
              </p>
              <p className="text-center font-semibold text-gray-900 dark:text-white mb-6">
                +91 {maskedPhone}
              </p>

              {/* OTP Input */}
              <div className="mb-6">
                <InputOTP
                  maxLength={6}
                  value={code}
                  onChange={(value) => {
                    setCode(value);
                    if (value.length === 6) {
                      handleComplete(value);
                    }
                  }}
                  disabled={isVerifying}
                >
                  <InputOTPGroup className="gap-2">
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                      <InputOTPSlot
                        key={index}
                        index={index}
                        className="w-12 h-14 text-lg font-semibold border-2 rounded-xl"
                      />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>

              {/* Error */}
              {error && (
                <div className="w-full bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3 mb-4">
                  <p className="text-sm text-red-600 dark:text-red-400 text-center">{error}</p>
                </div>
              )}

              {/* Verify Button */}
              <Button
                onClick={() => handleComplete(code)}
                disabled={code.length !== 6 || isVerifying}
                className="w-full h-14 bg-[#8bc43e] hover:bg-[#7ab32e] text-white rounded-xl font-semibold disabled:opacity-50 mb-4"
              >
                {isVerifying ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Logging in...</span>
                  </div>
                ) : (
                  'Verify & Login'
                )}
              </Button>

              {/* Resend */}
              <div className="text-center">
                {resendCountdown > 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Resend code in <span className="font-semibold text-gray-900 dark:text-white">{resendCountdown}s</span>
                  </p>
                ) : (
                  <button
                    onClick={handleResend}
                    disabled={isResending}
                    className="text-sm text-[#8bc43e] font-semibold hover:underline disabled:opacity-50"
                  >
                    {isResending ? 'Sending...' : 'Resend Code'}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
